import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';
import {
  Tenant,
  CompanyProfile,
  CreateTenantRequest,
  UpdateTenantRequest,
  ApiResponse,
  DropdownResponse,
  CountryItem,
  StateItem,
  CityItem,
} from '../types';

export interface TenantFilters {
  include_inactive?: boolean;
  page?: number;
  size?: number;
}

class TenantService {
  /**
   * Get all tenants (sub-tenants of current company)
   */
  async getTenants(filters?: TenantFilters): Promise<{ items: Tenant[], count?: number }> {
    const params: Record<string, any> = {};
    if (filters?.include_inactive !== undefined) {
      params.include_inactive = filters.include_inactive;
    }
    if (filters?.page !== undefined) {
      params.page = filters.page;
    }
    if (filters?.size !== undefined) {
      params.size = filters.size;
    }
    return apiClient.get<{ items: Tenant[], count?: number }>(API_CONFIG.ENDPOINTS.TENANTS, params);
  }

  /**
   * Get tenant by ID
   */
  async getTenantById(id: number): Promise<Tenant> {
    // Use plural endpoint /tenants/{id} for getting single tenant
    const response = await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.TENANTS}/${id}`);
    console.log('getTenantById response:', response);
    // Handle different API response formats
    if (response && typeof response === 'object') {
      if ('item' in response) return response.item;
      if ('data' in response) return response.data;
      if ('company_id' in response || 'company_name' in response) return response;
    }
    return response as Tenant;
  }

  /**
   * Get current company profile
   */
  async getCompanyProfile(): Promise<CompanyProfile> {
    return apiClient.get<CompanyProfile>(API_CONFIG.ENDPOINTS.COMPANY);
  }

  /**
   * Update own company profile
   */
  async updateCompanyProfile(data: UpdateTenantRequest): Promise<{ success: boolean; message: string }> {
    return apiClient.put<{ success: boolean; message: string }>(API_CONFIG.ENDPOINTS.COMPANY, data);
  }

  /**
   * Create a new tenant (sub-tenant)
   */
  async createTenant(data: CreateTenantRequest): Promise<Tenant> {
    return apiClient.post<Tenant>(API_CONFIG.ENDPOINTS.TENANTS, data);
  }

  /**
   * Update tenant
   */
  async updateTenant(id: number, data: UpdateTenantRequest): Promise<Tenant> {
    // Try singular endpoint /tenant/{id} for updates
    return apiClient.put<Tenant>(`${API_CONFIG.ENDPOINTS.TENANT}/${id}`, data);
  }

  /**
   * Delete/deactivate tenant
   */
  async deleteTenant(id: number): Promise<void> {
    return apiClient.delete<void>(`${API_CONFIG.ENDPOINTS.TENANT}/${id}`);
  }

  /**
   * Register a new top-level tenant (public, no auth)
   */
  async register(data: CreateTenantRequest): Promise<Tenant> {
    return apiClient.post<Tenant>(API_CONFIG.ENDPOINTS.REGISTER, data);
  }

  // ========== Dropdown APIs ==========

  /**
   * Get dropdown items by code type (requires auth)
   */
  async getDropdown(codeType: string, profileId: number = 1): Promise<DropdownResponse> {
    return apiClient.get<DropdownResponse>(
      `${API_CONFIG.ENDPOINTS.DROPDOWN}/${codeType}`,
      { profile_id: profileId }
    );
  }

  /**
   * Get public dropdown items by code type (no auth required)
   */
  async getPublicDropdown(codeType: string): Promise<DropdownResponse> {
    return apiClient.get<DropdownResponse>(
      `${API_CONFIG.ENDPOINTS.PUBLIC_DROPDOWN}/${codeType}`
    );
  }

  /**
   * Get business types (public endpoint, uses BUSI_TYPE code)
   */
  async getBusinessTypes(): Promise<DropdownResponse> {
    return this.getPublicDropdown('BUSI_TYPE');
  }

  /**
   * Get sub business types by business type ID
   * @param businessTypeId - The business type key (e.g., 68 for Trucking)
   */
  async getSubBusinessTypes(businessTypeId: number): Promise<DropdownResponse> {
    return apiClient.get<DropdownResponse>(
      `${API_CONFIG.ENDPOINTS.SUB_BUSINESS_TYPES}/${businessTypeId}`
    );
  }

  // ========== Geographic APIs (direct table access) ==========

  /**
   * Get countries list
   */
  async getCountries(search?: string): Promise<{ items: CountryItem[] }> {
    const params: Record<string, any> = {};
    if (search) {
      params.search = search;
    }
    return apiClient.get<{ items: CountryItem[] }>(API_CONFIG.ENDPOINTS.COUNTRIES, params);
  }

  /**
   * Get states by country
   */
  async getStates(countryId: number, search?: string): Promise<{ items: StateItem[] }> {
    const params: Record<string, any> = { country_id: countryId };
    if (search) {
      params.search = search;
    }
    return apiClient.get<{ items: StateItem[] }>(API_CONFIG.ENDPOINTS.STATES, params);
  }

  /**
   * Get cities by state
   */
  async getCities(stateId: number, search?: string): Promise<{ items: CityItem[] }> {
    const params: Record<string, any> = { state_id: stateId };
    if (search) {
      params.search = search;
    }
    return apiClient.get<{ items: CityItem[] }>(API_CONFIG.ENDPOINTS.CITIES, params);
  }
}

export const tenantService = new TenantService();
